import axios from 'axios'
import { fetchAuthSession, signOut } from 'aws-amplify/auth'
import { normalizeApiResponse } from '../utils/apiNormalization'
import { logger } from '../utils/logger'

// Environment-aware API base URL configuration
// Production: Always use relative path '/api' to route through CloudFront (avoids CORS errors)
// Development (localhost): Use VITE_API_URL to point directly to API Gateway for local testing
function getAPIBaseURL() {
  // Access location from globalThis to ensure it works in test environments
  const location = globalThis.window?.location || globalThis.location
  const hostname = location?.hostname || ''
  
  // Production environment detection: hostname contains 'elcapit.com'
  const isProduction = hostname.includes('elcapit.com')
  
  if (isProduction) {
    // Force relative path in production to route through CloudFront
    // This ensures same-origin requests and proper CORS handling
    return '/api'
  }
  
  // Development: Use VITE_API_URL (points to API Gateway) or fallback to '/api'
  return import.meta.env.VITE_API_URL || '/api'
}

// Export for testing
export { getAPIBaseURL }

// Create axios instance with initial baseURL
const api = axios.create({
  baseURL: getAPIBaseURL(),
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
  },
})

// Update baseURL dynamically for each request to ensure environment detection works correctly
// This is especially important for tests that mock window.location
api.interceptors.request.use((config) => {
  // Recalculate baseURL on each request to handle dynamic environment changes
  config.baseURL = getAPIBaseURL()
  return config
}, undefined, { synchronous: true, runWhen: () => true })

// Extract tenant context from subdomain or apex domain configuration
function getTenantContext() {
  const hostname = window.location.hostname
  const parts = hostname.split('.')

  // For local development - use environment variable or default
  if (hostname === 'localhost' || hostname.startsWith('127.0.0.1')) {
    const devTenant = import.meta.env.VITE_DEV_TENANT_ID

    if (!devTenant) {
      logger.warn('[API] VITE_DEV_TENANT_ID not set, using fallback tenant "dev-tenant"')
      return { subdomain: 'dev-tenant', tenantId: 'dev-tenant' }
    }

    logger.debug(`[API] Development mode - using tenant: ${devTenant}`)
    return { subdomain: devTenant, tenantId: devTenant }
  }

  // Extract subdomain (e.g., gym1.elcapit.com -> gym1)
  if (parts.length >= 3) {
    const subdomain = parts[0]
    logger.debug(`[API] Extracted tenant from subdomain: ${subdomain}`)
    return { subdomain, tenantId: subdomain }
  }

  // Apex domain (e.g., elcapit.com) - use configured fallback tenant
  // This is configured in .env.production as VITE_FALLBACK_TENANT_ID
  const fallbackTenant = import.meta.env.VITE_FALLBACK_TENANT_ID
  
  if (!fallbackTenant) {
    logger.error(`[API] Unable to extract subdomain from hostname "${hostname}" and VITE_FALLBACK_TENANT_ID is not configured. This will cause API requests to fail.`)
    return { subdomain: 'dev-tenant', tenantId: 'dev-tenant' }
  }
  
  logger.debug(`[API] Apex domain detected (${hostname}) - using configured tenant: ${fallbackTenant}`)
  return { subdomain: fallbackTenant, tenantId: fallbackTenant }
}


// Add auth token and tenant context to requests
api.interceptors.request.use(async (config) => {
  logger.debug('[API] Making request to:', config.url, 'Method:', config.method)
  
  try {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
      logger.debug('[API] Auth token added')
    } else {
      logger.warn('[API] No auth token available')
    }
  } catch (error) {
    logger.error('[API] Failed to get auth token:', error)
  }
  
  // Add tenant context headers
  const { subdomain, tenantId } = getTenantContext()
  logger.debug('[API] Tenant context:', { subdomain, tenantId })
  
  if (subdomain) {
    config.headers['X-Tenant-Subdomain'] = subdomain
  }
  // Always send X-Tenant-ID header if tenantId is available
  if (tenantId) {
    config.headers['X-Tenant-ID'] = tenantId
  }
  
  logger.debug('[API] Request headers:', config.headers)
  logger.debug('[API] Request data:', config.data)
  
  return config
})

// Handle errors
api.interceptors.response.use(
  (response) => {
    logger.debug('[API] Response received:', response.status, response.config.url)
    
    // Store original response data before normalization (preserves pagination metadata)
    if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
      response.data._original = { ...response.data }
    }
    
    // Normalize API response structure (paginated objects → arrays)
    logger.debug('[API] Before normalization:', response.data)
    response.data = normalizeApiResponse(response.data)
    logger.debug('[API] After normalization:', response.data)
    
    // Normalize member data: backend uses 'memberId', frontend expects 'id'
    if (response.data) {
      // Single member object
      if (response.data.memberId && !response.data.id) {
        response.data.id = response.data.memberId
      }
      // Array of members
      if (Array.isArray(response.data)) {
        response.data = response.data.map((item: any) => {
          if (item.memberId && !item.id) {
            return { ...item, id: item.memberId }
          }
          return item
        })
      }
      // Paginated response with items array (should not happen after normalization, but kept for safety)
      if (response.data.items && Array.isArray(response.data.items)) {
        response.data.items = response.data.items.map((item: any) => {
          if (item.memberId && !item.id) {
            return { ...item, id: item.memberId }
          }
          return item
        })
      }
    }
    
    return response
  },
  async (error) => {
    logger.error('[API] Request failed:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message
    })
    
    if (error.response?.status === 401 && !error.config._retry) {
      error.config._retry = true
      // Attempt a silent token refresh before giving up.
      // Cognito tokens expire after 1 hour; Amplify can silently get a new one
      // using the refresh token without requiring the user to log in again.
      try {
        logger.warn('[API] 401 received - attempting token refresh...')
        const session = await fetchAuthSession({ forceRefresh: true })
        const newToken = session.tokens?.idToken?.toString()
        if (newToken) {
          logger.warn('[API] Token refreshed - retrying original request')
          // Retry the original request with the new token using the configured api instance
          error.config.headers['Authorization'] = `Bearer ${newToken}`
          return api(error.config)
        }
      } catch (refreshError) {
        logger.error('[API] Token refresh failed:', refreshError)
      }
      // Refresh failed or no token — session is truly gone, redirect to login
      logger.warn('[API] Unauthorized - session expired, redirecting to login')
      await signOut().catch(() => {})
      const isEmbed = window.location.pathname.startsWith('/embed')
      window.location.href = isEmbed ? '/embed' : '/login'
    }
    return Promise.reject(error)
  }
)

// Member API
export const memberApi = {
  list: async () => {
    const response = await api.get('/members')
    // Ensure normalization even if interceptor is bypassed (e.g., in tests)
    response.data = normalizeApiResponse(response.data)
    return response
  },
  get: (id: string) => api.get(`/members/${id}`),
  create: (data: any) => api.post('/members', data),
  update: (id: string, data: any) => api.put(`/members/${id}`, data),
  delete: (id: string) => api.delete(`/members/${id}`),
  search: async (query: string) => {
    const response = await api.get(`/members?search=${query}`)
    // Ensure normalization even if interceptor is bypassed (e.g., in tests)
    response.data = normalizeApiResponse(response.data)
    return response
  },
}

// Check-In API
export const checkInApi = {
  create: (data: any) => api.post('/check-ins', data),
  list: (params?: any) => api.get('/check-ins', { params }),
  getByMember: (memberId: string) => api.get(`/check-ins/member/${memberId}`),
  classCheckIn: (classId: string, memberId: string) =>
    api.post('/check-ins/class', { classId, memberId }),
  getUpcomingClassBookings: (memberId: string, windowMinutes?: number) =>
    api.get('/check-ins/upcoming-classes', { params: { memberId, windowMinutes } }),
}

// POS API
export const posApi = {
  createTransaction: (data: any) => api.post('/pos/transactions', data),
  getProducts: () => api.get('/pos/products'),
  getTransactions: (params?: any) => api.get('/pos/transactions', { params }),
  // Stripe Terminal
  createConnectionToken: () => api.post('/pos/terminal/connection-token'),
  createPaymentIntent: (data: { amount: number; currency?: string; description?: string }) =>
    api.post('/pos/terminal/create-payment-intent', data),
  capturePaymentIntent: (data: { paymentIntentId: string }) =>
    api.post('/pos/terminal/capture-payment-intent', data),
  listReaders: () => api.get('/pos/terminal/readers'),
  registerReader: (data: { registrationCode: string; label?: string }) =>
    api.post('/pos/terminal/readers', data),
  createCheckoutSession: (data: { items: any[]; memberId?: string; transactionId?: string }) =>
    api.post('/pos/checkout-session', data),
}

// Booking API
export const bookingApi = {
  listClasses: (params?: any) => api.get('/classes', { params }),
  getClass: (id: string) => api.get(`/classes/${id}`),
  createClass: (data: any) => api.post('/classes', data),
  createBooking: (data: any) => api.post('/bookings', data),
  cancelBooking: (id: string, reason?: string) => api.delete(`/bookings/${id}`, { data: { reason } }),
  deleteClass: (id: string, reason?: string, cancelSeries?: boolean) => api.delete(`/classes/${id}`, { data: { reason, cancelSeries } }),
  getAttendees: (classId: string) => api.get(`/classes/${classId}/attendees`),
}

// Waiver API
export const waiverApi = {
  list: () => api.get('/waiver'),
  get: (id: string) => api.get(`/waiver/${id}`),
  create: (data: any) => api.post('/waiver', data),
  sign: (id: string, data: any) => api.post(`/waiver/${id}/sign`, data),
  signForMember: (data: any) => api.post('/waivers', data),
  getForMember: (memberId: string) => api.get(`/waivers/member/${memberId}`),
  checkStatus: (memberId: string) => api.get(`/waivers/check?memberId=${memberId}`),
}

// Waiver Settings API
export const waiverSettingsApi = {
  get: () => api.get('/waivers/settings'),
  update: (data: any) => api.put('/waivers/settings', data),
}

// Inventory API
export const inventoryApi = {
  list: () => api.get('/inventory'),
  get: (id: string) => api.get(`/inventory/${id}`),
  create: (data: any) => api.post('/inventory', data),
  update: (id: string, data: any) => api.put(`/inventory/${id}`, data),
  adjust: (id: string, data: any) => api.post(`/inventory/${id}/adjust`, data),
}

// Rental API
export const rentalApi = {
  listActive: () => api.get('/rentals/active'),
  checkout: (data: any) => api.post('/rentals/checkout', data),
  return: (data: any) => api.post('/rentals/return', data),
}

// Reporting API
export const reportingApi = {
  revenue: (params?: any) => api.get('/reporting/revenue', { params }),
  checkIns: (params?: any) => api.get('/reporting/check-ins', { params }),
  memberships: (params?: any) => api.get('/reporting/memberships', { params }),
  classes: (params?: any) => api.get('/reporting/classes', { params }),
  inventory: (params?: any) => api.get('/reporting/inventory', { params }),
  export: (reportData: any, format: 'csv' | 'pdf') => 
    api.post('/reports/export', { reportData, format }, { responseType: 'blob' }),
}

// Customer Portal API
export const customerApi = {
  getProfile: () => api.get('/customer/profile'),
  updateProfile: (data: any) => api.put('/customer/profile', data),
  getWaivers: () => api.get('/customer/waivers'),
  getWaiverContent: () => api.get('/customer/waiver-content'),
  getMembership: () => api.get('/customer/membership'),
  getPasses: () => api.get('/customer/passes'),
  getClasses: (params?: any) => api.get('/customer/classes', { params }),
  getBookings: () => api.get('/customer/bookings'),
  createBooking: (data: any) => api.post('/customer/bookings', data),
  cancelBooking: (id: string) => api.delete(`/customer/bookings/${id}`),
  getPlans: () => api.get('/customer/plans'),
  cancelMembership: () => api.post('/customer/membership/cancel', {}),
  pauseMembership: () => api.post('/customer/membership/pause', {}),
  resumeMembership: () => api.post('/customer/membership/resume', {}),
  signupForPlan: (planId: string) => api.post('/customer/membership/signup', { planId }),
  getPassTypes: () => api.get('/customer/pass-types'),
  getAllPasses: () => api.get('/customer/passes'),
  purchasePass: (passTypeId: string) => api.post('/customer/passes/purchase', { passTypeId }),
  getGiftCardTypes: () => api.get('/customer/gift-card-types'),
  purchaseGiftCard: (giftCardTypeId: string) => api.post('/customer/gift-cards/purchase', { giftCardTypeId }),
  getMyGiftCards: () => api.get('/customer/gift-cards'),
  getBillingPortalUrl: () => api.post('/customer/billing-portal'),
  // Climbing routes & sends
  getWalls: () => api.get('/customer/walls'),
  getRoutes: (params?: { wallId?: string; routeType?: string; grade?: string }) =>
    api.get('/customer/routes', { params }),
  getMySends: () => api.get('/customer/sends'),
  getMySendStats: () => api.get('/customer/sends/stats'),
  logSend: (routeId: string, data: {
    sendType?: string
    attempts?: number
    rating?: number | null
    comment?: string
  }) => api.post(`/customer/routes/${routeId}/sends`, data),
}

// Tenant API
export const tenantApi = {
  getInfo: () => api.get('/tenant/info'),
  getBranding: () => api.get('/tenant/branding'),
  updateBranding: (data: any) => api.put('/tenant/branding', data),
}

// Staff API
export const staffApi = {
  list: () => api.get('/staff'),
  get: (id: string) => api.get(`/staff/${id}`),
  create: (data: any) => api.post('/staff', data),
  update: (id: string, data: any) => api.put(`/staff/${id}`, data),
  delete: (id: string) => api.delete(`/staff/${id}`),
  listCertifications: (id: string) => api.get(`/staff/${id}/certifications`),
  addCertification: (id: string, data: any) => api.post(`/staff/${id}/certifications`, data),
  removeCertification: (id: string, certId: string) => api.delete(`/staff/${id}/certifications/${certId}`),
  getAvailability: (id: string) => api.get(`/staff/${id}/availability`),
  setAvailability: (id: string, data: any) => api.put(`/staff/${id}/availability`, data),
}

// Membership Plans API
export const membershipPlanApi = {
  list: () => api.get('/membership-plans'),
  get: (id: string) => api.get(`/membership-plans/${id}`),
  create: (data: any) => api.post('/membership-plans', data),
  update: (id: string, data: any) => api.put(`/membership-plans/${id}`, data),
  delete: (id: string) => api.delete(`/membership-plans/${id}`),
}

// Pass Types API (admin)
export const adminApi = {
  passTypes: {
    list: () => api.get('/pass-types'),
    create: (data: any) => api.post('/pass-types', data),
    update: (id: string, data: any) => api.put(`/pass-types/${id}`, data),
    delete: (id: string) => api.delete(`/pass-types/${id}`),
    assignToMember: (memberId: string, data: any) => api.post(`/members/${memberId}/passes`, data),
  },
  giftCardTypes: {
    list: () => api.get('/gift-card-types'),
    create: (data: any) => api.post('/gift-card-types', data),
    update: (id: string, data: any) => api.put(`/gift-card-types/${id}`, data),
    delete: (id: string) => api.delete(`/gift-card-types/${id}`),
  },
}

// Locations API
export const locationApi = {
  list: () => api.get('/locations'),
  get: (id: string) => api.get(`/locations/${id}`),
  create: (data: any) => api.post('/locations', data),
  update: (id: string, data: any) => api.put(`/locations/${id}`, data),
  delete: (id: string) => api.delete(`/locations/${id}`),
}

// Certification Types API
export const certificationTypeApi = {
  list: () => api.get('/certification-types'),
  get: (id: string) => api.get(`/certification-types/${id}`),
  create: (data: any) => api.post('/certification-types', data),
  update: (id: string, data: any) => api.put(`/certification-types/${id}`, data),
  delete: (id: string) => api.delete(`/certification-types/${id}`),
}

// Member Certifications API
export const memberCertificationApi = {
  list: (memberId: string) => api.get(`/members/${memberId}/certifications`),
  add: (memberId: string, data: { certificationType: string; issueDate: string; expirationDate?: string; notes?: string }) =>
    api.post(`/members/${memberId}/certifications`, data),
  update: (memberId: string, certId: string, data: any) => api.put(`/members/${memberId}/certifications/${certId}`, data),
  revoke: (memberId: string, certId: string) => api.delete(`/members/${memberId}/certifications/${certId}`),
}

// Membership Assignment API
export const membershipAssignmentApi = {
  // Create new assignment
  create: (data: {
    memberId: string
    planId: string
    startDate: string
    notes?: string
  }) => api.post('/membership-assignments', data),
  
  // List all assignments (with optional filters)
  list: (params?: {
    status?: 'active' | 'expired' | 'cancelled' | 'pending'
    expiring?: boolean
  }) => api.get('/membership-assignments', { params }),
  
  // Get specific assignment
  get: (id: string) => api.get(`/membership-assignments/${id}`),
  
  // Update assignment
  update: (id: string, data: {
    startDate?: string
    endDate?: string
    status?: 'active' | 'expired' | 'cancelled' | 'pending'
    notes?: string
  }) => api.put(`/membership-assignments/${id}`, data),
  
  // Cancel active assignment
  cancel: (id: string, data?: {
    cancellationReason?: string
  }) => api.post(`/membership-assignments/${id}/cancel`, data),
  
  // Renew expired assignment
  renew: (id: string) => api.post(`/membership-assignments/${id}/renew`),
  
  // Get member's assignment history
  getMemberAssignments: (memberId: string) => 
    api.get(`/members/${memberId}/membership-assignments`),
  
  // Get member's active membership
  getActiveMembership: (memberId: string) => 
    api.get(`/members/${memberId}/active-membership`),
}

// Member Identification API
export const memberIdentificationApi = {
  // Upload member photo
  uploadPhoto: (memberId: string, file: File) => {
    const formData = new FormData()
    formData.append('photo', file)
    return api.post(`/members/${memberId}/photo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  // Get member photo URL
  getPhoto: (memberId: string) => 
    api.get(`/members/${memberId}/photo`),

  // Delete member photo
  deletePhoto: (memberId: string) => 
    api.delete(`/members/${memberId}/photo`),

  // Generate member barcodes
  generateBarcodes: (memberId: string) => 
    api.post(`/members/${memberId}/barcodes/generate`),

  // Get member barcode URLs
  getBarcodes: (memberId: string) => 
    api.get(`/members/${memberId}/barcodes`),

  // Get member card data (includes photo and barcodes)
  getMemberCard: (memberId: string) => 
    api.get(`/members/${memberId}/card`),

  // Barcode scan check-in
  scanBarcode: (barcodeImage: string, locationId: string) => 
    api.post('/check-in/scan', {
      barcodeImage,
      locationId,
    }),
}

// Public API — unauthenticated endpoints (no JWT required)
// Used for self-service member registration from the embedded portal.
const publicApi = axios.create({
  headers: { 'Content-Type': 'application/json' },
})

publicApi.interceptors.request.use((config) => {
  config.baseURL = getAPIBaseURL()
  const { tenantId, subdomain } = getTenantContext()
  if (tenantId) config.headers['X-Tenant-ID'] = tenantId
  if (subdomain) config.headers['X-Tenant-Subdomain'] = subdomain
  return config
}, undefined, { synchronous: true })

export const passwordResetApi = {
  forgotPassword: (tenantId: string, email: string) =>
    publicApi.post('/forgot-password', { tenantId, email }),

  confirmForgotPassword: (token: string, newPassword: string) =>
    publicApi.post('/confirm-forgot-password', { token, newPassword }),
}

export const memberRegistrationApi = {
  register: (data: {
    name: string
    email: string
    phone: string
    password: string
    waiverAgreed: boolean
    waiverSignature: string
  }) => publicApi.post('/public/register', data),

  getWaiverContent: () => publicApi.get('/public/waiver-content'),
}

// Time Clock API
export const timeClockApi = {
  // Kiosk: PIN-based clock in/out (public, no JWT)
  clock: (data: { pin: string; jobType?: string; locationId?: string }) =>
    publicApi.post('/timeclock/clock', data),

  // Authenticated staff/admin endpoints
  listActive: () => api.get('/timeclock/active'),
  listRecords: (params?: { staffId?: string; startDate?: string; endDate?: string }) =>
    api.get('/timeclock/records', { params }),
  getRecord: (recordId: string) => api.get(`/timeclock/records/${recordId}`),
  createRecord: (data: {
    staffId: string
    clockInTime: string
    clockOutTime?: string
    jobType?: string
    locationId?: string
    notes?: string
  }) => api.post('/timeclock/records', data),
  updateRecord: (recordId: string, data: {
    clockInTime?: string
    clockOutTime?: string
    jobType?: string
    locationId?: string
    notes?: string
  }) => api.put(`/timeclock/records/${recordId}`, data),
  deleteRecord: (recordId: string) => api.delete(`/timeclock/records/${recordId}`),
  splitRecord: (recordId: string, splitTime: string) =>
    api.post(`/timeclock/records/${recordId}/split`, { splitTime }),
  timesheet: (params?: { staffId?: string; startDate?: string; endDate?: string }) =>
    api.get('/timeclock/reports/timesheet', { params }),

  // Set staff PIN (admin)
  setPin: (staffId: string, pin: string) =>
    api.put(`/staff/${staffId}/timeclock-pin`, { pin }),
}

// Youth Programs API
export const youthProgramsApi = {
  // Programs
  listPrograms: () => api.get('/youth-programs'),
  getProgram: (id: string) => api.get(`/youth-programs/${id}`),
  createProgram: (data: any) => api.post('/youth-programs', data),
  updateProgram: (id: string, data: any) => api.put(`/youth-programs/${id}`, data),
  deleteProgram: (id: string) => api.delete(`/youth-programs/${id}`),

  // Groups
  listGroups: (programId: string) => api.get(`/youth-programs/${programId}/groups`),
  getGroup: (id: string) => api.get(`/youth-groups/${id}`),
  createGroup: (data: any) => api.post('/youth-groups', data),
  updateGroup: (id: string, data: any) => api.put(`/youth-groups/${id}`, data),
  deleteGroup: (id: string) => api.delete(`/youth-groups/${id}`),

  // Participants
  listParticipants: (groupId: string) => api.get(`/youth-groups/${groupId}/participants`),
  addParticipant: (groupId: string, data: any) => api.post(`/youth-groups/${groupId}/participants`, data),
  updateParticipant: (id: string, data: any) => api.put(`/youth-participants/${id}`, data),
  removeParticipant: (id: string) => api.delete(`/youth-participants/${id}`),

  // Bulk email copy
  copyEmails: (groupId: string) => api.post(`/youth-groups/${groupId}/copy-emails`, {}),

  // Attendance
  recordAttendance: (groupId: string, data: { sessionDate: string; records: any[]; sessionId?: string }) =>
    api.post(`/youth-groups/${groupId}/attendance`, data),
  listAttendance: (groupId: string, params?: { from?: string; to?: string; participantId?: string }) =>
    api.get(`/youth-groups/${groupId}/attendance`, { params }),
  getAttendanceSummary: (groupId: string) =>
    api.get(`/youth-groups/${groupId}/attendance-summary`),

  // Group billing
  createGroupInvoices: (groupId: string, data: { amount: number; description?: string; dueDate?: string; participantIds?: string[] }) =>
    api.post(`/youth-groups/${groupId}/invoices`, data),
  listGroupInvoices: (groupId: string) =>
    api.get(`/youth-groups/${groupId}/invoices`),
  updateInvoice: (invoiceId: string, data: any) =>
    api.put(`/youth-invoices/${invoiceId}`, data),
}

// QuickBooks Integration API
export const quickbooksApi = {
  getAuthUrl: () => api.get('/integrations/quickbooks/auth-url'),
  getStatus: () => api.get('/integrations/quickbooks/status'),
  sync: () => api.post('/integrations/quickbooks/sync'),
  disconnect: () => api.delete('/integrations/quickbooks/disconnect'),
}

// Stripe Connect Integration API
export const stripeConnectApi = {
  getAuthUrl: () => api.get('/integrations/stripe/auth-url'),
  getStatus: () => api.get('/integrations/stripe/status'),
  disconnect: () => api.delete('/integrations/stripe/disconnect'),
}

// Customer Segments API
export const segmentApi = {
  list: () => api.get('/segments'),
  get: (id: string) => api.get(`/segments/${id}`),
  create: (data: any) => api.post('/segments', data),
  update: (id: string, data: any) => api.put(`/segments/${id}`, data),
  delete: (id: string) => api.delete(`/segments/${id}`),
  getMembers: (id: string) => api.get(`/segments/${id}/members`),
  assignMember: (memberId: string, segmentId: string) =>
    api.post(`/members/${memberId}/segments`, { segmentId }),
  removeMember: (memberId: string, segmentId: string) =>
    api.delete(`/members/${memberId}/segments/${segmentId}`),
  getMemberSegments: (memberId: string) => api.get(`/members/${memberId}/segments`),
}

// Promotions API
export const promotionApi = {
  list: () => api.get('/promotions'),
  get: (id: string) => api.get(`/promotions/${id}`),
  create: (data: any) => api.post('/promotions', data),
  update: (id: string, data: any) => api.put(`/promotions/${id}`, data),
  delete: (id: string) => api.delete(`/promotions/${id}`),
  createCode: (id: string, data: any) => api.post(`/promotions/${id}/codes`, data),
  listCodes: (id: string) => api.get(`/promotions/${id}/codes`),
  calculatePricing: (data: any) => api.post('/promotions/calculate', data),
  validateCode: (code: string) => api.post('/promotions/validate-code', { code }),
}

// Document Validation API
export const documentApi = {
  upload: (memberId: string, data: any) => api.post(`/members/${memberId}/documents`, data),
  listForMember: (memberId: string) => api.get(`/members/${memberId}/documents`),
  listPendingReviews: () => api.get('/document-reviews'),
  approve: (docId: string) => api.post(`/document-reviews/${docId}/approve`),
  reject: (docId: string, reason: string) =>
    api.post(`/document-reviews/${docId}/reject`, { reason }),
}

// Purchase Orders API
export const purchaseOrderApi = {
  list: (params?: any) => api.get('/purchase-orders', { params }),
  get: (id: string) => api.get(`/purchase-orders/${id}`),
  create: (data: any) => api.post('/purchase-orders', data),
  update: (id: string, data: any) => api.put(`/purchase-orders/${id}`, data),
  submit: (id: string) => api.post(`/purchase-orders/${id}/submit`),
  receive: (id: string, data: any) => api.post(`/purchase-orders/${id}/receive`, data),
  delete: (id: string) => api.delete(`/purchase-orders/${id}`),
}

// Stock Transfer API
export const stockTransferApi = {
  list: () => api.get('/stock-transfers'),
  create: (data: any) => api.post('/stock-transfers', data),
  complete: (id: string) => api.post(`/stock-transfers/${id}/complete`),
}

// Supplier API
export const supplierApi = {
  list: () => api.get('/suppliers'),
  create: (data: any) => api.post('/suppliers', data),
  update: (id: string, data: any) => api.put(`/suppliers/${id}`, data),
  delete: (id: string) => api.delete(`/suppliers/${id}`),
}

// Storefront API (public, no auth required)
export const storefrontApi = {
  getProducts: () => publicApi.get('/storefront/products'),
  getProduct: (id: string) => publicApi.get(`/storefront/product/${id}`),
  createCart: () => publicApi.post('/storefront/cart'),
  getCart: (token: string) => publicApi.get(`/storefront/cart/${token}`),
  updateCart: (token: string, data: any) => publicApi.put(`/storefront/cart/${token}`, data),
  applyCode: (token: string, code: string) =>
    publicApi.post(`/storefront/cart/${token}/apply-code`, { code }),
  checkout: (data: any) => publicApi.post('/storefront/checkout', data),
}

// Lead Management / CRM API
export const leadApi = {
  list: (params?: { status?: string; source?: string; assignedTo?: string }) => api.get('/leads', { params }),
  get: (id: string) => api.get(`/leads/${id}`),
  create: (data: any) => api.post('/leads', data),
  update: (id: string, data: any) => api.put(`/leads/${id}`, data),
  archive: (id: string) => api.delete(`/leads/${id}`),
  addFollowUp: (id: string, data: any) => api.post(`/leads/${id}/follow-ups`, data),
  getFollowUps: (id: string) => api.get(`/leads/${id}/follow-ups`),
  updateFollowUp: (id: string, fid: string, data: any) => api.put(`/leads/${id}/follow-ups/${fid}`, data),
  addNote: (id: string, data: any) => api.post(`/leads/${id}/notes`, data),
  convert: (id: string, data: any) => api.post(`/leads/${id}/convert`, data),
  getStats: () => api.get('/leads/stats'),
  getDueFollowUps: () => api.get('/leads/follow-ups/due'),
}

// Incident Reporting API
export const incidentApi = {
  list: (params?: { type?: string; severity?: string; status?: string; startDate?: string; endDate?: string }) =>
    api.get('/incidents', { params }),
  get: (id: string) => api.get(`/incidents/${id}`),
  create: (data: any) => api.post('/incidents', data),
  update: (id: string, data: any) => api.put(`/incidents/${id}`, data),
  addNote: (id: string, data: any) => api.post(`/incidents/${id}/notes`, data),
  close: (id: string, data: any) => api.post(`/incidents/${id}/close`, data),
  getStats: () => api.get('/incidents/stats'),
  export: (params?: { startDate?: string; endDate?: string }) => api.get('/incidents/export', { params }),
  getMemberIncidents: (memberId: string) => api.get(`/members/${memberId}/incidents`),
}

// Competition API
export const competitionApi = {
  list: (params?: { status?: string }) => api.get('/competitions', { params }),
  get: (id: string) => api.get(`/competitions/${id}`),
  create: (data: any) => api.post('/competitions', data),
  update: (id: string, data: any) => api.put(`/competitions/${id}`, data),
  cancel: (id: string) => api.delete(`/competitions/${id}`),
  addRoutes: (id: string, data: any) => api.post(`/competitions/${id}/routes`, data),
  getRoutes: (id: string) => api.get(`/competitions/${id}/routes`),
  updateRoute: (id: string, routeNum: string, data: any) => api.put(`/competitions/${id}/routes/${routeNum}`, data),
  register: (id: string, data: any) => api.post(`/competitions/${id}/register`, data),
  listRegistrations: (id: string) => api.get(`/competitions/${id}/registrations`),
  updateRegistration: (id: string, regId: string, data: any) => api.put(`/competitions/${id}/registrations/${regId}`, data),
  cancelRegistration: (id: string, regId: string) => api.delete(`/competitions/${id}/registrations/${regId}`),
  submitScores: (id: string, data: any) => api.post(`/competitions/${id}/scores`, data),
  getScores: (id: string) => api.get(`/competitions/${id}/scores`),
  getLeaderboard: (id: string, params?: { category?: string }) => api.get(`/competitions/${id}/leaderboard`, { params }),
  finalize: (id: string) => api.post(`/competitions/${id}/finalize`),
  getResults: (id: string) => api.get(`/competitions/${id}/results`),
}

// Household/Family API
export const householdApi = {
  list: () => api.get('/households'),
  get: (id: string) => api.get(`/households/${id}`),
  create: (data: any) => api.post('/households', data),
  update: (id: string, data: any) => api.put(`/households/${id}`, data),
  delete: (id: string) => api.delete(`/households/${id}`),
  addMember: (id: string, data: any) => api.post(`/households/${id}/members`, data),
  removeMember: (id: string, memberId: string) => api.delete(`/households/${id}/members/${memberId}`),
  changePrimary: (id: string, data: any) => api.put(`/households/${id}/primary`, data),
  getMemberHousehold: (memberId: string) => api.get(`/members/${memberId}/household`),
}

// Membership Freeze API
export const freezeApi = {
  create: (assignmentId: string, data: any) => api.post(`/membership-assignments/${assignmentId}/freeze`, data),
  listForAssignment: (assignmentId: string) => api.get(`/membership-assignments/${assignmentId}/freezes`),
  listAll: (params?: { status?: string }) => api.get('/freezes', { params }),
  listForMember: (memberId: string) => api.get(`/members/${memberId}/freezes`),
  update: (id: string, data: any) => api.put(`/freezes/${id}`, data),
  cancel: (id: string, data: any) => api.post(`/freezes/${id}/cancel`, data),
  getSettings: () => api.get('/freeze-settings'),
  updateSettings: (data: any) => api.put('/freeze-settings', data),
}

// Route & Wall Management API
export const wallApi = {
  list: () => api.get('/walls'),
  create: (data: any) => api.post('/walls', data),
  update: (id: string, data: any) => api.put(`/walls/${id}`, data),
  delete: (id: string) => api.delete(`/walls/${id}`),
}

export const routeApi = {
  list: (params?: { wallId?: string; grade?: string; status?: string; routeType?: string; color?: string; zone?: string }) =>
    api.get('/routes', { params }),
  get: (id: string) => api.get(`/routes/${id}`),
  create: (data: any) => api.post('/routes', data),
  update: (id: string, data: any) => api.put(`/routes/${id}`, data),
  retire: (id: string) => api.delete(`/routes/${id}`),
  getSends: (id: string) => api.get(`/routes/${id}/sends`),
  logSend: (id: string, data: any) => api.post(`/routes/${id}/sends`, data),
  getMemberSends: (memberId: string) => api.get(`/members/${memberId}/sends`),
  getMap: (locationId: string) => api.get(`/routes/map/${locationId}`),
  getStats: () => api.get('/routes/stats'),
}

export const analyticsApi = {
  getSummary: (params?: { startDate?: string; endDate?: string }) =>
    api.get('/analytics/summary', { params }),
}

export const privateEventsApi = {
  list: (params?: { status?: string; startDate?: string; endDate?: string; eventType?: string }) =>
    api.get('/private-events', { params }),
  get: (id: string) => api.get(`/private-events/${id}`),
  create: (data: any) => api.post('/private-events', data),
  update: (id: string, data: any) => api.put(`/private-events/${id}`, data),
  delete: (id: string) => api.delete(`/private-events/${id}`),
}

export const routeResetApi = {
  list: () => api.get('/route-resets'),
  create: (data: any) => api.post('/route-resets', data),
  update: (id: string, data: any) => api.put(`/route-resets/${id}`, data),
}

export const wallClosureApi = {
  list: (params?: { wallId?: string; status?: string; from?: string; to?: string }) =>
    api.get('/wall-closures', { params }),
  create: (data: any) => api.post('/wall-closures', data),
  update: (id: string, data: any) => api.put(`/wall-closures/${id}`, data),
  delete: (id: string) => api.delete(`/wall-closures/${id}`),
  getWallStatus: () => api.get('/wall-status'),
}

export default api
